/**
 * MultiQrPreview Component
 *
 * The main container component that orchestrates the multi-preview display.
 * Displays up to three QR code previews simultaneously (site, fixed bay, super bay)
 * based on the site's bay configuration.
 *
 * Features:
 * - Always displays the Site QR preview (Requirement 1.2)
 * - Conditionally displays Fixed Bay QR preview when site has fixed bays (Requirement 1.3)
 * - Conditionally displays Super Bay QR preview when site has super bays (Requirement 1.4)
 * - Shows Bay Selector dropdowns when multiple bays exist (Requirements 2.1, 2.2)
 * - Displays bays without selector when only one bay exists (Requirements 2.3, 2.4)
 * - Updates preview immediately on bay selection (Requirement 2.5)
 * - Provides individual download buttons for each preview
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 2.1, 2.2, 2.3, 2.4, 2.5
 */

import { useCallback, useState } from 'react';
import type { QrTemplate } from '../types';
import type { AdvancedOptions } from '../types/advancedOptions';
import type { FixedBay, SuperBay } from '../../site/models/domainModels';
import type { PreviewConfig } from '../utils/templateModifier';
import { useMultiPreview } from '../hooks/useMultiPreview';
import { QrPreviewCard } from './QrPreviewCard';
import { BaySelector } from './BaySelector';
import { QrCodeDialog } from '../../qr/components/QrCodeDialog';
import { useAuth } from '../../auth/useAuth';
import { useAnalytics } from '../../analytics/useAnalytics';
import type { QrIndividualDownloadAttributes } from '../../analytics/trackingTypes';
import { embedPngMetadata, buildQrMetadata } from '../utils/pngMetadata';
import './MultiQrPreview.css';

/**
 * Props for the MultiQrPreview component
 */
export interface MultiQrPreviewProps {
  /** The current QR template with customization settings */
  readonly template: QrTemplate;
  /** The site identifier */
  readonly siteId: string;
  /** The site name for filename generation */
  readonly siteName: string;
  /** Array of fixed bays for the site */
  readonly fixedBays: readonly FixedBay[];
  /** Array of super bays for the site */
  readonly superBays: readonly SuperBay[];
  /** Function to ensure a valid site key exists */
  readonly ensureKey: () => Promise<string>;
  /** Advanced options for URL generation */
  readonly advancedOptions?: AdvancedOptions;
}

/**
 * Gets the display label for a fixed bay
 * Format: "Bay {id}" (e.g., "Bay 5", "Bay 10")
 */
function getFixedBayLabel(bay: FixedBay): string {
  return `Bay ${bay.id}`;
}

/**
 * Gets the display label for a super bay
 * Uses the name (either the original name or generated "Grass tee N" from the adapter)
 */
function getSuperBayLabel(bay: SuperBay): string {
  return bay.name;
}

/**
 * Gets the unique identifier for a fixed bay
 */
function getFixedBayId(bay: FixedBay): string {
  return bay.id;
}

/**
 * Gets the unique identifier for a super bay
 */
function getSuperBayId(bay: SuperBay): string {
  return bay.id;
}

/**
 * Comparator for sorting fixed bays by bay ID (ascending)
 * Compares numerically when both IDs are numeric, falls back to locale string comparison.
 * Requirement 2.6: Display fixed bay numbers in ascending order
 */
function sortFixedBays(a: FixedBay, b: FixedBay): number {
  const numA = Number(a.id);
  const numB = Number(b.id);
  if (Number.isFinite(numA) && Number.isFinite(numB)) {
    return numA - numB;
  }
  return a.id.localeCompare(b.id);
}

/**
 * Comparator for sorting super bays by ID (ascending)
 * Compares numerically when both IDs are numeric, falls back to locale string comparison.
 * Requirement 5.1: Display super bays sorted by ID
 */
function sortSuperBays(a: SuperBay, b: SuperBay): number {
  const numA = Number(a.id);
  const numB = Number(b.id);
  if (Number.isFinite(numA) && Number.isFinite(numB)) {
    return numA - numB;
  }
  return a.id.localeCompare(b.id);
}

/**
 * Triggers a browser download for the given data URL with embedded metadata
 */
function triggerDownloadWithMetadata(
  dataUrl: string,
  filename: string,
  title: string,
  url: string,
  author: string
): void {
  const metadata = buildQrMetadata(title, url, author);
  const dataUrlWithMetadata = embedPngMetadata(dataUrl, metadata);
  
  const link = document.createElement('a');
  link.href = dataUrlWithMetadata;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Sanitizes a string to be safe for use as a filename
 */
function sanitizeFilename(name: string): string {
  if (!name) return 'unnamed';
  return name
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .replace(/[\s.]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'unnamed';
}

/**
 * Full-size QR code output size in pixels (matches batch generator)
 */
const DOWNLOAD_SIZE = 1024;

/**
 * State for the QR code dialog
 */
interface DialogState {
  readonly isOpen: boolean;
  readonly imageUrl: string;
  readonly fullSizeImageUrl: string | null;
  readonly isLoadingFullSize: boolean;
  readonly label: string;
  readonly downloadFilename: string;
  readonly encodedUrl: string;
  readonly previewConfig: PreviewConfig | null;
}

const initialDialogState: DialogState = {
  isOpen: false,
  imageUrl: '',
  fullSizeImageUrl: null,
  isLoadingFullSize: false,
  label: '',
  downloadFilename: '',
  encodedUrl: '',
  previewConfig: null,
};

/**
 * Generates a full-size QR code for download
 * Uses the same size as batch generator (1024px) for consistency
 */
async function generateFullSizeQrCode(
  template: QrTemplate,
  previewConfig: PreviewConfig,
  url: string
): Promise<string> {
  const { generateQrMatrix, calculateOverlaySize } = await import('../generator/qrGenerator');
  const { renderQrToDataUrl } = await import('../generator/canvasRenderer');
  const { getEffectiveTemplate } = await import('../utils/templateModifier');

  const effectiveTemplate = getEffectiveTemplate(template, previewConfig);
  const matrix = generateQrMatrix(url, 'H');
  const overlaySize = effectiveTemplate.centerEnabled
    ? calculateOverlaySize(DOWNLOAD_SIZE)
    : 0;

  const bayNumber = effectiveTemplate.showCenterLabel
    ? previewConfig.bayNumber
    : undefined;

  const logoDataUrl = effectiveTemplate.showLogo
    ? effectiveTemplate.logoDataUrl
    : null;

  return renderQrToDataUrl(matrix, DOWNLOAD_SIZE, effectiveTemplate.color, {
    centerEnabled: effectiveTemplate.centerEnabled,
    overlaySize,
    logoDataUrl,
    bayNumber,
    fontFamily: effectiveTemplate.fontFamily,
    fontSize: effectiveTemplate.fontSize,
    bayLabelColor: effectiveTemplate.bayLabelColor,
    centerPadding: effectiveTemplate.centerPadding,
    cornerLabelText: effectiveTemplate.cornerLabelText,
    moduleCount: matrix.size,
    bottomLogoEnabled: effectiveTemplate.bottomLogo,
  });
}

/**
 * MultiQrPreview - Displays multiple QR code previews simultaneously
 *
 * This component orchestrates the display of up to three QR code previews
 * based on the site's bay configuration. It uses the useMultiPreview hook
 * for state management and renders QrPreviewCard components for each preview.
 *
 * Layout:
 * - Site QR is always displayed first
 * - Fixed Bay QR is displayed when the site has fixed bays
 * - Super Bay QR is displayed when the site has super bays
 *
 * @example
 * ```tsx
 * <MultiQrPreview
 *   template={template}
 *   siteId="site-123"
 *   fixedBays={fixedBays}
 *   superBays={superBays}
 *   ensureKey={ensureKey}
 * />
 * ```
 */
export function MultiQrPreview({
  template,
  siteId,
  siteName,
  fixedBays,
  superBays,
  ensureKey,
  advancedOptions,
}: MultiQrPreviewProps): JSX.Element {
  const { state, setSelectedFixedBay, setSelectedSuperBay } = useMultiPreview(
    template,
    siteId,
    siteName,
    fixedBays,
    superBays,
    ensureKey,
    advancedOptions
  );

  const [dialogState, setDialogState] = useState<DialogState>(initialDialogState);
  const { user } = useAuth();
  const { track } = useAnalytics();

  // Get author from user profile (name or email)
  const author = user?.profile?.name ?? user?.profile?.email ?? 'Unknown';

  /**
   * Tracks an individual QR code download event.
   * Fires "Pocket Admin QR Individual Download" with bay type, bay ID, and download source.
   */
  const trackIndividualDownload = useCallback(
    (bayType: QrIndividualDownloadAttributes['BayType'], bayId: string | null, fromDialog: boolean): void => {
      track('Pocket Admin QR Individual Download', {
        SiteId: siteId,
        BayType: bayType,
        BayId: bayId,
        FromDialog: fromDialog,
      });
    },
    [track, siteId]
  );

  const hasFixedBays = fixedBays.length > 0;
  const hasSuperBays = superBays.length > 0;
  const hasMultipleFixedBays = fixedBays.length > 1;
  const hasMultipleSuperBays = superBays.length > 1;

  // Get the label for the currently selected fixed bay
  const fixedBayLabel = state.selectedFixedBay
    ? getFixedBayLabel(state.selectedFixedBay)
    : '';

  // Get the label for the currently selected super bay
  const superBayLabel = state.selectedSuperBay
    ? getSuperBayLabel(state.selectedSuperBay)
    : '';

  // Download handlers for each preview
  // Each handler generates a full-size (1024px) QR code before downloading,
  // since the preview images are only 512px.
  const handleDownloadSite = useCallback(async () => {
    if (state.sitePreview.dataUrl && state.sitePreview.url) {
      trackIndividualDownload('site', null, false);
      const filename = `${sanitizeFilename(siteName)}_site_qr.png`;
      const title = `${siteName} - Site QR`;
      const previewConfig: PreviewConfig = { type: 'site', siteName };

      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.sitePreview.url);
        triggerDownloadWithMetadata(fullSizeDataUrl, filename, title, state.sitePreview.url, author);
      } catch (error) {
        console.error('Failed to generate full-size QR code for download, falling back to preview size:', error);
        triggerDownloadWithMetadata(state.sitePreview.dataUrl, filename, title, state.sitePreview.url, author);
      }
    }
  }, [state.sitePreview.dataUrl, state.sitePreview.url, siteName, author, template, trackIndividualDownload]);

  const handleDownloadFixedBay = useCallback(async () => {
    if (state.fixedBayPreview.dataUrl && state.fixedBayPreview.url && state.selectedFixedBay) {
      trackIndividualDownload('fixed-bay', state.selectedFixedBay.id, false);
      const bayLabel = getFixedBayLabel(state.selectedFixedBay);
      const filename = `${sanitizeFilename(siteName)}_${sanitizeFilename(state.selectedFixedBay.id)}_qr.png`;
      const title = `${siteName} - ${bayLabel}`;
      const previewConfig: PreviewConfig = { type: 'fixed-bay', bayId: state.selectedFixedBay.id, bayNumber: state.selectedFixedBay.id };

      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.fixedBayPreview.url);
        triggerDownloadWithMetadata(fullSizeDataUrl, filename, title, state.fixedBayPreview.url, author);
      } catch (error) {
        console.error('Failed to generate full-size QR code for download, falling back to preview size:', error);
        triggerDownloadWithMetadata(state.fixedBayPreview.dataUrl, filename, title, state.fixedBayPreview.url, author);
      }
    }
  }, [state.fixedBayPreview.dataUrl, state.fixedBayPreview.url, state.selectedFixedBay, siteName, fixedBays, author, template, trackIndividualDownload]);

  const handleDownloadSuperBay = useCallback(async () => {
    if (state.superBayPreview.dataUrl && state.superBayPreview.url && state.selectedSuperBay) {
      trackIndividualDownload('super-bay', state.selectedSuperBay.id, false);
      const bayLabel = getSuperBayLabel(state.selectedSuperBay);
      const filename = `${sanitizeFilename(siteName)}_${sanitizeFilename(state.selectedSuperBay.id)}_qr.png`;
      const title = `${siteName} - ${bayLabel}`;
      const previewConfig: PreviewConfig = { type: 'super-bay', bayId: state.selectedSuperBay.id, bayName: bayLabel };

      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.superBayPreview.url);
        triggerDownloadWithMetadata(fullSizeDataUrl, filename, title, state.superBayPreview.url, author);
      } catch (error) {
        console.error('Failed to generate full-size QR code for download, falling back to preview size:', error);
        triggerDownloadWithMetadata(state.superBayPreview.dataUrl, filename, title, state.superBayPreview.url, author);
      }
    }
  }, [state.superBayPreview.dataUrl, state.superBayPreview.url, state.selectedSuperBay, siteName, author, template, trackIndividualDownload]);

  // Dialog click handlers - generate full-size image when opening
  const handleSiteClick = useCallback(async () => {
    if (state.sitePreview.dataUrl && state.sitePreview.url) {
      const previewConfig: PreviewConfig = { type: 'site', siteName };
      
      // Open dialog immediately with preview image, then load full-size
      setDialogState({
        isOpen: true,
        imageUrl: state.sitePreview.dataUrl,
        fullSizeImageUrl: null,
        isLoadingFullSize: true,
        label: siteName,
        downloadFilename: `${sanitizeFilename(siteName)}_site_qr.png`,
        encodedUrl: state.sitePreview.url,
        previewConfig,
      });

      // Generate full-size image in background
      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.sitePreview.url);
        setDialogState((prev) => ({
          ...prev,
          fullSizeImageUrl: fullSizeDataUrl,
          isLoadingFullSize: false,
        }));
      } catch (error) {
        console.error('Failed to generate full-size QR code:', error);
        setDialogState((prev) => ({ ...prev, isLoadingFullSize: false }));
      }
    }
  }, [state.sitePreview.dataUrl, state.sitePreview.url, siteName, template]);

  const handleFixedBayClick = useCallback(async () => {
    if (state.fixedBayPreview.dataUrl && state.fixedBayPreview.url && state.selectedFixedBay) {
      const bayLabel = getFixedBayLabel(state.selectedFixedBay);
      const previewConfig: PreviewConfig = { type: 'fixed-bay', bayId: state.selectedFixedBay.id, bayNumber: state.selectedFixedBay.id };

      // Open dialog immediately with preview image, then load full-size
      setDialogState({
        isOpen: true,
        imageUrl: state.fixedBayPreview.dataUrl,
        fullSizeImageUrl: null,
        isLoadingFullSize: true,
        label: `${siteName} - ${bayLabel}`,
        downloadFilename: `${sanitizeFilename(siteName)}_${sanitizeFilename(state.selectedFixedBay.id)}_qr.png`,
        encodedUrl: state.fixedBayPreview.url,
        previewConfig,
      });

      // Generate full-size image in background
      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.fixedBayPreview.url);
        setDialogState((prev) => ({
          ...prev,
          fullSizeImageUrl: fullSizeDataUrl,
          isLoadingFullSize: false,
        }));
      } catch (error) {
        console.error('Failed to generate full-size QR code:', error);
        setDialogState((prev) => ({ ...prev, isLoadingFullSize: false }));
      }
    }
  }, [state.fixedBayPreview.dataUrl, state.fixedBayPreview.url, state.selectedFixedBay, siteName, fixedBays, template]);

  const handleSuperBayClick = useCallback(async () => {
    if (state.superBayPreview.dataUrl && state.superBayPreview.url && state.selectedSuperBay) {
      const bayLabel = getSuperBayLabel(state.selectedSuperBay);
      const previewConfig: PreviewConfig = { type: 'super-bay', bayId: state.selectedSuperBay.id, bayName: bayLabel };

      // Open dialog immediately with preview image, then load full-size
      setDialogState({
        isOpen: true,
        imageUrl: state.superBayPreview.dataUrl,
        fullSizeImageUrl: null,
        isLoadingFullSize: true,
        label: `${siteName} - ${bayLabel}`,
        downloadFilename: `${sanitizeFilename(siteName)}_${sanitizeFilename(state.selectedSuperBay.id)}_qr.png`,
        encodedUrl: state.superBayPreview.url,
        previewConfig,
      });

      // Generate full-size image in background
      try {
        const fullSizeDataUrl = await generateFullSizeQrCode(template, previewConfig, state.superBayPreview.url);
        setDialogState((prev) => ({
          ...prev,
          fullSizeImageUrl: fullSizeDataUrl,
          isLoadingFullSize: false,
        }));
      } catch (error) {
        console.error('Failed to generate full-size QR code:', error);
        setDialogState((prev) => ({ ...prev, isLoadingFullSize: false }));
      }
    }
  }, [state.superBayPreview.dataUrl, state.superBayPreview.url, state.selectedSuperBay, siteName, template]);

  const handleCloseDialog = useCallback(() => {
    setDialogState(initialDialogState);
  }, []);

  // Dialog download handler - uses cached full-size QR code or generates one
  const handleDialogDownload = useCallback(async () => {
    if (!dialogState.encodedUrl) {
      return;
    }

    // Track the individual download from dialog
    if (dialogState.previewConfig) {
      const bayType = dialogState.previewConfig.type === 'site' ? 'site' as const
        : dialogState.previewConfig.type === 'fixed-bay' ? 'fixed-bay' as const
        : 'super-bay' as const;
      const bayId = dialogState.previewConfig.type === 'site' ? null : dialogState.previewConfig.bayId ?? null;
      trackIndividualDownload(bayType, bayId, true);
    }

    try {
      // Use cached full-size image if available, otherwise generate
      const fullSizeDataUrl = dialogState.fullSizeImageUrl
        ?? (dialogState.previewConfig
          ? await generateFullSizeQrCode(template, dialogState.previewConfig, dialogState.encodedUrl)
          : dialogState.imageUrl);

      // Embed metadata and trigger download
      const metadata = buildQrMetadata(dialogState.label, dialogState.encodedUrl, author);
      const dataUrlWithMetadata = embedPngMetadata(fullSizeDataUrl, metadata);

      const link = document.createElement('a');
      link.href = dataUrlWithMetadata;
      link.download = dialogState.downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // Fallback to preview-size image if full-size generation fails
      console.error('Failed to generate full-size QR code:', error);
      const metadata = buildQrMetadata(dialogState.label, dialogState.encodedUrl, author);
      const dataUrlWithMetadata = embedPngMetadata(dialogState.imageUrl, metadata);

      const link = document.createElement('a');
      link.href = dataUrlWithMetadata;
      link.download = dialogState.downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [dialogState, template, author, trackIndividualDownload]);

  return (
    <div className="multi-qr-preview">
      {/* Site QR Preview - Always displayed (Requirement 1.2) */}
      <div className="multi-qr-preview__item">
        <QrPreviewCard
          label="Site"
          dataUrl={state.sitePreview.dataUrl}
          isGenerating={state.sitePreview.isGenerating}
          error={state.sitePreview.error}
          url={state.sitePreview.url}
          onDownload={handleDownloadSite}
          onClick={handleSiteClick}
        />
      </div>

      {/* Fixed Bay QR Preview - Displayed when site has fixed bays (Requirement 1.3) */}
      {hasFixedBays && state.selectedFixedBay && (
        <div className="multi-qr-preview__item">
          <QrPreviewCard
            label={fixedBayLabel}
            dataUrl={state.fixedBayPreview.dataUrl}
            isGenerating={state.fixedBayPreview.isGenerating}
            error={state.fixedBayPreview.error}
            url={state.fixedBayPreview.url}
            onDownload={handleDownloadFixedBay}
            onClick={handleFixedBayClick}
          />
          {/* Bay Selector - Only shown when multiple fixed bays exist (Requirements 2.1, 2.3) */}
          {hasMultipleFixedBays && (
            <BaySelector
              bays={fixedBays}
              selectedBay={state.selectedFixedBay}
              onBayChange={setSelectedFixedBay}
              getLabel={(bay) => getFixedBayLabel(bay)}
              getId={getFixedBayId}
              sortBy={sortFixedBays}
            />
          )}
        </div>
      )}

      {/* Super Bay QR Preview - Displayed when site has super bays (Requirement 1.4) */}
      {hasSuperBays && state.selectedSuperBay && (
        <div className="multi-qr-preview__item">
          <QrPreviewCard
            label={superBayLabel}
            dataUrl={state.superBayPreview.dataUrl}
            isGenerating={state.superBayPreview.isGenerating}
            error={state.superBayPreview.error}
            url={state.superBayPreview.url}
            onDownload={handleDownloadSuperBay}
            onClick={handleSuperBayClick}
          />
          {/* Bay Selector - Only shown when multiple super bays exist (Requirements 2.2, 2.4) */}
          {hasMultipleSuperBays && (
            <BaySelector
              bays={superBays}
              selectedBay={state.selectedSuperBay}
              onBayChange={setSelectedSuperBay}
              getLabel={getSuperBayLabel}
              getId={getSuperBayId}
              sortBy={sortSuperBays}
            />
          )}
        </div>
      )}

      {/* QR Code Dialog for enlarged view */}
      {dialogState.isOpen && (
        <QrCodeDialog
          imageUrl={dialogState.fullSizeImageUrl ?? dialogState.imageUrl}
          siteName={dialogState.label}
          downloadFilename={dialogState.downloadFilename}
          encodedUrl={dialogState.encodedUrl}
          author={author}
          onClose={handleCloseDialog}
          onDownload={handleDialogDownload}
          isFullSize={dialogState.fullSizeImageUrl !== null}
        />
      )}
    </div>
  );
}
